// File: js/money/business-date.js
//
// Business-date arithmetic at a FIXED UTC+01:00 (WAT). Imports nothing (spec §8).
//
// Cameroon has never observed DST, so a fixed offset is exact — and it is the only
// way to get an answer that does not depend on the machine's TZ database or locale.
// `Intl` and the local-time Date accessors are deliberately unused: a report run on a
// laptop set to Europe/Paris must produce the same business date as the server.
//
// This module exists to kill audit H1: `reporting.js:66-69` builds the start bound from
// `new Date("YYYY-MM-DD").toISOString()` (UTC midnight = 01:00 WAT) and the end bound
// from `setHours(23,59,59,999)` (local). The window is therefore local [01:00 … 23:59:59]
// and every sale taken between local 00:00 and 01:00 falls in NO report, ever.

/** Fixed WAT offset in milliseconds. No DST — see module header. */
const WAT_OFFSET_MS = 60 * 60 * 1000;

/** One whole day, in milliseconds. */
const DAY_MS = 24 * 60 * 60 * 1000;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Pads an integer to `width` digits without ever truncating.
 * @param {number} n
 * @param {number} width
 * @returns {string}
 */
function pad(n, width) {
    return String(n).padStart(width, '0');
}

/**
 * Formats a UTC instant as `YYYY-MM-DD` using only UTC accessors, so the result is
 * independent of the host timezone. Not `toISOString()`: that switches to an expanded
 * (`+275760-…`) year format at the extremes, which would silently change the shape.
 * @param {number} epochMs
 * @returns {string}
 */
function utcDateString(epochMs) {
    const d = new Date(epochMs);
    return `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1, 2)}-${pad(d.getUTCDate(), 2)}`;
}

/**
 * The business date (`YYYY-MM-DD`, WAT) an instant belongs to.
 *
 * Invariant 9: `business_date` derives from `device_time` at fixed UTC+01:00.
 *
 * @param {string} isoString ISO-8601 instant, e.g. `'2026-07-27T23:30:00.000Z'`.
 * @returns {string} `YYYY-MM-DD` in WAT.
 * @throws {TypeError} If the argument is not a parseable ISO string (programmer error —
 *   data-level problems are surfaced by the folds, never raised).
 */
export function businessDateOf(isoString) {
    if (typeof isoString !== 'string') {
        throw new TypeError(`businessDateOf expects an ISO string, received ${typeof isoString}`);
    }
    const epochMs = Date.parse(isoString);
    if (Number.isNaN(epochMs)) {
        throw new TypeError(`businessDateOf received an unparseable instant: ${isoString}`);
    }
    // Shift the instant into WAT, then read the calendar day off it in UTC.
    return utcDateString(epochMs + WAT_OFFSET_MS);
}

/**
 * The UTC instants bounding one WAT business day, as a HALF-OPEN interval.
 *
 * `endIso` is EXCLUSIVE and is exactly the next day's `startIso`, so consecutive days
 * tile with no gap and no overlap. Queries must therefore be `>= startIso` and
 * `< endIso` — an inclusive `lte` end bound is precisely how H1 lost an hour and how a
 * midnight-exact row would otherwise be counted on both days.
 *
 * @param {string} dateStr `YYYY-MM-DD` business date (WAT).
 * @returns {{startIso: string, endIso: string}} UTC instants; `endIso` exclusive.
 * @throws {TypeError} If `dateStr` is not a well-formed calendar date.
 */
export function businessDayBounds(dateStr) {
    if (typeof dateStr !== 'string' || !DATE_RE.test(dateStr)) {
        throw new TypeError(`businessDayBounds expects a YYYY-MM-DD string, received ${dateStr}`);
    }
    const year = Number(dateStr.slice(0, 4));
    const month = Number(dateStr.slice(5, 7));
    const day = Number(dateStr.slice(8, 10));

    const localMidnightUtc = Date.UTC(year, month - 1, day);
    // Date.UTC silently rolls 2026-02-31 over to March; refuse rather than answer for a
    // day the caller did not ask about.
    if (utcDateString(localMidnightUtc) !== dateStr) {
        throw new TypeError(`businessDayBounds received a non-existent date: ${dateStr}`);
    }

    const startMs = localMidnightUtc - WAT_OFFSET_MS;
    return {
        startIso: new Date(startMs).toISOString(),
        endIso: new Date(startMs + DAY_MS).toISOString()
    };
}
